/**
 * [Phase 2b-C §5.2 R1, R2] Step 3 - Scoring Rules
 *
 * Full scoring customization UI. Defaults mirror the backend
 * SCORING_RULE_DEFAULTS (engines/scoring.py):
 *   - passing_int = -2.0 (R1: matches existing half_ppr preset)
 *   - sack_allowed = 0 (new rule key; off by default)
 *   - reception = 0.5 (half-PPR baseline)
 *   - points-allowed DST tiers remain hardcoded in the engine (TD-4)
 *
 * IDP scoring section: code present below, **dead-conditional on any of
 * form.roster.{s,cb,lb,dl} > 0**. Per R2 those rows are disabled and always
 * zero in 2b, so this section NEVER renders. Phase 6 flips the gate.
 *
 * Persistence (per §5.2): on any deviation from half_ppr defaults, we set
 *   form.scoring_rules = { ...overrides... } (only the changed keys)
 * and the wizard's buildSettings() forwards scoring_rules to POST /api/leagues.
 * When all values match half_ppr, scoring_rules is emitted as {} (the backend
 * treats empty as "use preset"); the legacy scoring_format stays at the
 * matching preset name. Otherwise scoring_format flips to "custom".
 */
import { useMemo, useState } from "react";
import { Lock, RotateCcw } from "lucide-react";

// Canonical half-PPR defaults - must mirror backend SCORING_RULE_DEFAULTS.
export const HALF_PPR_DEFAULTS = {
  // Passing
  passing_yds_per_pt: 25,
  passing_td: 6,
  passing_int: -2, // R1
  passing_2pt: 2,
  sack_allowed: 0,
  // Rushing
  rushing_yds_per_pt: 10,
  rushing_td: 6,
  rushing_2pt: 2,
  // Receiving
  receiving_yds_per_pt: 10,
  receiving_td: 6,
  receiving_2pt: 2,
  reception: 0.5,
  // Misc
  fumble_lost: -2,
  // Kicker
  fg_under_40: 3,
  fg_40_to_49: 4,
  fg_50_plus: 5,
  extra_point: 1,
  fg_missed: -1,
  // DST team-level
  dst_sack: 1,
  dst_int: 2,
  dst_fumble_rec: 2,
  dst_def_td: 6,
  dst_safety: 2,
};

// Other presets for the "Reset to" menu.
const STANDARD_DEFAULTS = { ...HALF_PPR_DEFAULTS, reception: 0 };
const FULL_PPR_DEFAULTS = { ...HALF_PPR_DEFAULTS, reception: 1 };

// Section groupings for UI.
const SECTIONS = [
  {
    title: "Passing",
    keys: [
      { key: "passing_yds_per_pt", label: "Yards / point", step: 1 },
      { key: "passing_td", label: "Passing TD", step: 1 },
      { key: "passing_int", label: "Interception Thrown", step: 1 },
      { key: "passing_2pt", label: "2-pt Conversion", step: 1 },
      { key: "sack_allowed", label: "Sack Taken (QB)", step: 0.5 },
    ],
  },
  {
    title: "Rushing",
    keys: [
      { key: "rushing_yds_per_pt", label: "Yards / point", step: 1 },
      { key: "rushing_td", label: "Rushing TD", step: 1 },
      { key: "rushing_2pt", label: "2-pt Conversion", step: 1 },
    ],
  },
  {
    title: "Receiving",
    keys: [
      { key: "reception", label: "Reception (PPR)", step: 0.5 },
      { key: "receiving_yds_per_pt", label: "Yards / point", step: 1 },
      { key: "receiving_td", label: "Receiving TD", step: 1 },
      { key: "receiving_2pt", label: "2-pt Conversion", step: 1 },
    ],
  },
  {
    title: "Misc Offense",
    keys: [{ key: "fumble_lost", label: "Fumble Lost", step: 1 }],
  },
  {
    title: "Kicker",
    keys: [
      { key: "fg_under_40", label: "FG < 40 yds", step: 1 },
      { key: "fg_40_to_49", label: "FG 40-49 yds", step: 1 },
      { key: "fg_50_plus", label: "FG 50+ yds", step: 1 },
      { key: "extra_point", label: "Extra Point Made", step: 1 },
      { key: "fg_missed", label: "FG Missed", step: 1 },
    ],
  },
  {
    title: "Defense / Special Teams (team-level)",
    keys: [
      { key: "dst_sack", label: "Sack", step: 0.5 },
      { key: "dst_int", label: "Interception", step: 1 },
      { key: "dst_fumble_rec", label: "Fumble Recovery", step: 1 },
      { key: "dst_def_td", label: "Defensive TD", step: 1 },
      { key: "dst_safety", label: "Safety", step: 1 },
    ],
  },
];

// IDP section - dead-conditional per R2. Retained so Phase 6 flips the gate.
const IDP_SECTION = {
  title: "Individual Defensive Players (IDP)",
  keys: [
    { key: "idp_tackle_solo", label: "Solo Tackle", step: 0.5 },
    { key: "idp_sack", label: "Sack", step: 0.5 },
    { key: "idp_int", label: "Interception", step: 1 },
    { key: "idp_forced_fumble", label: "Forced Fumble", step: 1 },
    { key: "idp_def_td", label: "Defensive TD", step: 1 },
  ],
};

const PRESET_LABEL = {
  standard: "Standard (non-PPR)",
  half_ppr: "Half-PPR",
  full_ppr: "Full PPR",
};
const PRESETS = {
  standard: STANDARD_DEFAULTS,
  half_ppr: HALF_PPR_DEFAULTS,
  full_ppr: FULL_PPR_DEFAULTS,
};

/** Given current rules, return the matching preset key or null.
 *  Checks half_ppr first (the default) so a baseline load is stable. */
export function detectPreset(rules) {
  const full = { ...HALF_PPR_DEFAULTS, ...(rules || {}) };
  const PRESET_ORDER = ["half_ppr", "full_ppr", "standard"];
  for (const name of PRESET_ORDER) {
    const preset = PRESETS[name];
    const match = Object.keys(HALF_PPR_DEFAULTS).every(
      (k) => Number(full[k]) === Number(preset[k]),
    );
    if (match) return name;
  }
  return null;
}

/** Strip keys equal to half_ppr default - only persist actual overrides. */
export function pruneToOverrides(rules) {
  const out = {};
  for (const [k, v] of Object.entries(rules || {})) {
    if (Number(v) !== Number(HALF_PPR_DEFAULTS[k])) out[k] = Number(v);
  }
  return out;
}

/**
 * [Phase 2c-D §6.2.1 Gotcha (b)] Inverse of `pruneToOverrides` for Copy Settings.
 * Hydrates a stored `(scoring_format, scoring_rules)` pair back into the form's
 * full rule dict shape. Returns a fresh object - does not mutate inputs.
 *
 *   scoring_format = "half_ppr" + scoring_rules = {} → full HALF_PPR_DEFAULTS
 *   scoring_format = "standard" + scoring_rules = {} → full STANDARD_DEFAULTS
 *   scoring_format = "full_ppr" + scoring_rules = {} → full FULL_PPR_DEFAULTS
 *   scoring_format = "custom"   + scoring_rules = {pass_td:6,...} →
 *       HALF_PPR_DEFAULTS extended with the overrides
 *   Unknown format or null inputs → HALF_PPR_DEFAULTS as safe fallback.
 */
export function expandPreset(scoring_format, scoring_rules) {
  const overrides = scoring_rules || {};
  if (scoring_format === "custom") {
    return { ...HALF_PPR_DEFAULTS, ...overrides };
  }
  if (scoring_format && PRESETS[scoring_format]) {
    // Stored format is a preset name. If the stored rules dict happens to have
    // any deltas, those still win over the preset baseline.
    return { ...PRESETS[scoring_format], ...overrides };
  }
  return { ...HALF_PPR_DEFAULTS, ...overrides };
}

const NumberInput = ({ value, onChange, step, dataTestId }) => (
  <input
    type="number"
    step={step}
    value={Number.isFinite(value) ? value : ""}
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === "" || raw === "-") {
        onChange(raw);
        return;
      }
      const num = Number(raw);
      if (Number.isFinite(num)) onChange(num);
    }}
    data-testid={dataTestId}
    className="w-24 bg-[#111111] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-3 py-1.5 font-mono text-sm text-right text-[#F5F5F5] outline-none transition-colors"
  />
);

export default function Step3Scoring({ form, update }) {
  // Live rules = half_ppr defaults ⨁ form overrides.
  const rules = useMemo(
    () => ({ ...HALF_PPR_DEFAULTS, ...(form.scoring_rules || {}) }),
    [form.scoring_rules],
  );
  const activePreset = detectPreset(rules);

  const [mode, setMode] = useState("customize"); // "customize" | "import"

  const setRule = (key, value) => {
    update({ scoring_rules: { ...form.scoring_rules, [key]: value } });
  };

  const resetToPreset = (presetName) => {
    // Clear all overrides → falls back to preset defaults.
    // Because our defaults here are half_ppr, only emit deltas for non-half-PPR presets.
    const preset = PRESETS[presetName];
    const overrides = {};
    for (const [k, v] of Object.entries(preset)) {
      if (Number(v) !== Number(HALF_PPR_DEFAULTS[k])) overrides[k] = v;
    }
    update({ scoring_rules: overrides });
  };

  // [§5.2 R2] IDP section renders only when a configured IDP roster slot > 0.
  // Per R2 those slots are disabled + always zero; this never renders in 2b.
  const showIdpSection =
    (form.roster?.s ?? 0) +
      (form.roster?.cb ?? 0) +
      (form.roster?.lb ?? 0) +
      (form.roster?.dl ?? 0) >
    0;

  // [Phase 2b-D polish] "Summary at a glance" - sticky header.
  // When a preset is detected, override-count is 0 (full match). When no preset
  // matches, count overrides vs the closest preset (half_ppr) and label as
  // "Half-PPR · N custom rules" rather than "Custom · ..." per testing-agent
  // iteration_14 redline. Keeps the baseline preset label visible.
  const overridesVsHalfPpr = pruneToOverrides(form.scoring_rules);
  const activePresetName = detectPreset(form.scoring_rules || {});
  const overrideCount =
    activePresetName !== null ? 0 : Object.keys(overridesVsHalfPpr).length;
  const summaryLabel = (() => {
    if (overrideCount === 0) {
      return PRESET_LABEL[activePresetName || "half_ppr"];
    }
    // No exact preset match → label as "Half-PPR · N custom rule(s)"
    return `${PRESET_LABEL.half_ppr} · ${overrideCount} custom rule${
      overrideCount === 1 ? "" : "s"
    }`;
  })();

  return (
    <div data-testid="wizard-step-3" className="space-y-8">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Scoring Rules
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          Customize how your league scores fantasy points.
        </p>
        <p className="font-body text-xs text-[#A0A0A0]/70 mt-2 italic">
          Note: Real ranking customization based on your scoring changes lands in Phase 6.
        </p>
      </header>

      {/* [Phase 2b-D polish] Sticky "Summary at a glance" header */}
      <div
        data-testid="wizard-scoring-summary-header"
        className="sticky top-0 z-10 -mx-1 px-3 py-2 bg-[#111111]/95 backdrop-blur border-b border-[#252525] flex items-center justify-between gap-3"
      >
        <span className="font-body text-xs uppercase tracking-wider text-[#A0A0A0]">
          Summary
        </span>
        <span
          data-testid="wizard-scoring-summary-text"
          className={`font-display font-bold text-sm uppercase tracking-wide ${
            overrideCount > 0 ? "text-[#FF6B00]" : "text-[#F5F5F5]"
          }`}
        >
          {summaryLabel}
        </span>
      </div>

      {/* Two top buttons. */}
      <section className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          data-testid="wizard-scoring-mode-customize"
          onClick={() => setMode("customize")}
          className={`rounded-lg border-2 px-4 py-3 text-left transition-all ${
            mode === "customize"
              ? "border-[#FF6B00] bg-[#FF6B00]/10"
              : "border-[#252525] bg-[#1C1C1C] hover:border-[#FF6B00]/40"
          }`}
        >
          <p className="font-display font-bold text-base text-[#F5F5F5]">Customize Scoring</p>
          <p className="font-body text-xs text-[#A0A0A0] mt-0.5">Fine-tune every rule below.</p>
        </button>
        <button
          type="button"
          disabled
          data-testid="wizard-scoring-mode-import"
          title="TD-32 - Phase 2d/e"
          className="rounded-lg border-2 border-[#252525] bg-[#1C1C1C]/40 px-4 py-3 text-left opacity-40 cursor-not-allowed"
        >
          <p className="font-display font-bold text-base text-[#F5F5F5] flex items-center gap-2">
            Import League Scoring Settings
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#A0A0A0] font-body">
              <Lock size={9} /> Coming Soon
            </span>
          </p>
          <p className="font-body text-xs text-[#A0A0A0] mt-0.5">
            Pull from Sleeper / ESPN / Yahoo - TD-32.
          </p>
        </button>
      </section>

      {mode === "customize" && (
        <>
          {/* Preset switcher + reset */}
          <section className="flex flex-wrap items-center gap-3 bg-[#1C1C1C] border border-[#252525] rounded-xl p-4">
            <p className="font-body text-xs uppercase tracking-wider text-[#A0A0A0]">
              Quick preset:
            </p>
            {Object.keys(PRESETS).map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`wizard-scoring-preset-${p}`}
                onClick={() => resetToPreset(p)}
                className={`font-display font-bold text-xs uppercase px-3 py-1.5 rounded transition-colors ${
                  activePreset === p
                    ? "bg-[#FF6B00] text-white"
                    : "bg-[#252525] text-[#A0A0A0] hover:bg-[#333]"
                }`}
              >
                {PRESET_LABEL[p]}
              </button>
            ))}
            <span className="ml-auto">
              <button
                type="button"
                data-testid="wizard-scoring-reset-defaults"
                onClick={() => resetToPreset("half_ppr")}
                className="inline-flex items-center gap-2 font-body text-xs text-[#A0A0A0] hover:text-[#FF6B00] transition-colors"
              >
                <RotateCcw size={12} /> Reset to Defaults
              </button>
            </span>
          </section>

          {/* Scoring rule sections */}
          {SECTIONS.map((section) => (
            <section
              key={section.title}
              data-testid={`wizard-scoring-section-${section.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-[#1C1C1C] border border-[#252525] rounded-xl overflow-hidden"
            >
              <header className="px-4 py-2 bg-[#252525]/40 border-b border-[#252525]">
                <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0]">
                  {section.title}
                </p>
              </header>
              <div className="divide-y divide-[#252525]">
                {section.keys.map(({ key, label, step }) => {
                  const v = rules[key];
                  const isOverride =
                    form.scoring_rules && form.scoring_rules[key] !== undefined;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between px-4 py-2.5"
                    >
                      <div>
                        <p className="font-body text-sm text-[#F5F5F5]">
                          {label}
                          {isOverride && (
                            <span className="ml-2 text-[9px] font-display font-bold uppercase tracking-wider text-[#FF6B00]">
                              modified
                            </span>
                          )}
                        </p>
                        <p className="font-body text-[10px] text-[#A0A0A0]">
                          default: {HALF_PPR_DEFAULTS[key]}
                        </p>
                      </div>
                      <NumberInput
                        value={v}
                        step={step}
                        onChange={(val) => setRule(key, val)}
                        dataTestId={`wizard-scoring-${key}-input`}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {/* [R2] IDP scoring - dead-conditional. Never renders in 2b. */}
          {showIdpSection && (
            <section
              data-testid="wizard-scoring-idp-section"
              className="bg-[#1C1C1C] border border-[#252525] rounded-xl overflow-hidden"
            >
              <header className="px-4 py-2 bg-[#252525]/40 border-b border-[#252525]">
                <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0]">
                  {IDP_SECTION.title}
                </p>
              </header>
              <div className="divide-y divide-[#252525]">
                {IDP_SECTION.keys.map(({ key, label, step }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between px-4 py-2.5"
                  >
                    <div>
                      <p className="font-body text-sm text-[#F5F5F5]">{label}</p>
                    </div>
                    <NumberInput
                      value={rules[key]}
                      step={step}
                      onChange={(val) => setRule(key, val)}
                      dataTestId={`wizard-scoring-${key}-input`}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export const validateStep3 = (form) => {
  const rules = form.scoring_rules || {};
  for (const [k, v] of Object.entries(rules)) {
    const num = Number(v);
    if (!Number.isFinite(num)) return `Scoring rule "${k}" must be a number.`;
    if (Math.abs(num) > 100)
      return `Scoring rule "${k}" is out of range (-100 .. 100).`;
  }
  return null;
};
